import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, ArrowUpDown, PanelLeftClose, ListOrdered } from "lucide-react";

export const RANK_FILTERS = [
  { v: "all", l: "Show All" },
  { v: "top5", l: "Top 5" },
  { v: "top10", l: "Top 10" },
  { v: "top20", l: "Top 20" },
  { v: "top25", l: "Top 25" },
  { v: "top50", l: "Top 50" },
  { v: "r90", l: "Rating ≥ 90" },
  { v: "r85", l: "Rating ≥ 85" },
];

// Prominent one-tap rank chips (Top 25 is the highlighted default recommendation).
const QUICK_FILTERS = [
  { v: "all", l: "All 100" },
  { v: "top25", l: "Top 25", primary: true },
  { v: "top50", l: "Top 50" },
];

const SORT_OPTIONS = [
  { v: "rank", l: "Overall Rank" },
  { v: "financial", l: "Financial" },
  { v: "impact", l: "Impact" },
  { v: "prestige", l: "Prestige" },
  { v: "sustainability", l: "Sustainability" },
  { v: "brand", l: "Brand" },
];

export function Leaderboard({
  institutions,
  activeRank,
  onHover,
  onSelect,
  compareMode,
  compareList,
  rankFilter,
  onRankFilter,
  conference,
  onConference,
  conferences = [],
  instType,
  onInstType,
  sortKey = "rank",
  onSortKey,
  totalCount,
  collapsible = false,
  collapsed = false,
  onToggleCollapse,
}) {
  const [query, setQuery] = useState("");
  const [kbdIndex, setKbdIndex] = useState(0);
  const listRef = useRef(null);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = !q
      ? institutions
      : institutions.filter(
          (i) =>
            i.name.toLowerCase().includes(q) ||
            i.short.toLowerCase().includes(q) ||
            `${i.city} ${i.state}`.toLowerCase().includes(q)
        );
    if (sortKey !== "rank") {
      list = list.slice().sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        if (av == null && bv == null) return a.rank - b.rank;
        if (av == null) return 1;
        if (bv == null) return -1;
        return bv - av;
      });
    }
    return list;
  }, [institutions, query, sortKey]);

  useEffect(() => {
    setKbdIndex(0);
  }, [query, rankFilter, conference, instType, sortKey]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-kbd-index="${kbdIndex}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [kbdIndex]);

  const onSearchKey = (e) => {
    if (rows.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setKbdIndex((i) => Math.min(rows.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setKbdIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      onSelect(rows[Math.min(kbdIndex, rows.length - 1)] || rows[0]);
    }
  };

  const showPillar = sortKey !== "rank" && !compareMode;

  if (collapsible && collapsed) {
    return (
      <button
        className="lb-collapsed-tab"
        data-testid="leaderboard-expand"
        onClick={onToggleCollapse}
        title="Show ranked index"
      >
        <ListOrdered size={16} strokeWidth={2} />
        Ranked Index
      </button>
    );
  }

  return (
    <aside className="hud-leaderboard" data-testid="leaderboard-panel">
      <div className="hud-panel-head">
        {collapsible && (
          <button
            className="lb-collapse-btn"
            data-testid="leaderboard-collapse"
            onClick={onToggleCollapse}
            aria-label="Hide ranked index"
            title="Hide ranked index"
          >
            <PanelLeftClose size={16} strokeWidth={2} />
          </button>
        )}
        <span className="hud-eyebrow">Ranked Index</span>
        <h2 className="hud-panel-title">Too Big to Fail</h2>
        <p className="hud-panel-note">
          {compareMode
            ? "Compare mode · pick two"
            : `Showing ${rows.length} of ${totalCount} institutions`}
        </p>

        <div className="lb-search" data-testid="search-wrap">
          <Search size={15} strokeWidth={1.9} className="lb-search-icon" />
          <input
            className="lb-search-input"
            data-testid="search-input"
            placeholder="Search universities…"
            value={query}
            list="lb-suggestions"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onSearchKey}
          />
          <datalist id="lb-suggestions">
            {institutions.map((i) => (
              <option key={i.rank} value={i.name} />
            ))}
          </datalist>
          {query && (
            <button
              className="lb-search-clear"
              data-testid="search-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Quick rank chips — Top 25 is the highlighted recommendation */}
        <div className="lb-quickchips" data-testid="quick-filters">
          {QUICK_FILTERS.map((f) => (
            <button
              key={f.v}
              className={`lb-chip-btn${rankFilter === f.v ? " lb-chip-btn-on" : ""}${
                f.primary ? " lb-chip-btn-primary" : ""
              }`}
              data-testid={`quick-filter-${f.v}`}
              onClick={() => onRankFilter(f.v)}
            >
              {f.l}
            </button>
          ))}
        </div>

        <div className="lb-filter-grid">
          <div className="lb-filter">
            <label className="lb-filter-label">Conference</label>
            <select
              className="lb-filter-select"
              data-testid="conference-filter"
              value={conference}
              onChange={(e) => onConference(e.target.value)}
            >
              <option value="all">All Conferences</option>
              {conferences.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="lb-filter">
            <label className="lb-filter-label">Type</label>
            <div className="lb-toggle" data-testid="type-toggle">
              {["all", "Public", "Private"].map((t) => (
                <button
                  key={t}
                  className={`lb-toggle-btn${instType === t ? " lb-toggle-btn-on" : ""}`}
                  data-testid={`type-${t.toLowerCase()}`}
                  onClick={() => onInstType(t)}
                >
                  {t === "all" ? "All" : t}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="lb-filter-grid">
          <div className="lb-filter">
            <label className="lb-filter-label">Rank Filter</label>
            <select
              className="lb-filter-select"
              data-testid="rank-filter"
              value={rankFilter}
              onChange={(e) => onRankFilter(e.target.value)}
            >
              {RANK_FILTERS.map((f) => (
                <option key={f.v} value={f.v}>
                  {f.l}
                </option>
              ))}
            </select>
          </div>

          <div className="lb-filter">
            <label className="lb-filter-label">
              <ArrowUpDown size={11} strokeWidth={2} /> Sort By
            </label>
            <select
              className="lb-filter-select"
              data-testid="sort-select"
              value={sortKey}
              onChange={(e) => onSortKey(e.target.value)}
            >
              {SORT_OPTIONS.map((s) => (
                <option key={s.v} value={s.v}>
                  {s.l}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="hud-leaderboard-list" data-testid="leaderboard-list" ref={listRef}>
        {rows.length === 0 && (
          <p className="lb-empty" data-testid="leaderboard-empty">
            No institutions match your filters.
          </p>
        )}
        {rows.map((inst, idx) => {
          const cmpIndex = compareList.indexOf(inst.rank);
          const active = compareMode ? cmpIndex >= 0 : activeRank === inst.rank;
          const kbd = idx === kbdIndex;
          const pillarVal = inst[sortKey];
          return (
            <button
              key={inst.rank}
              data-kbd-index={idx}
              className={`lb-row${active ? " lb-row-active" : ""}${
                kbd ? " lb-row-kbd" : ""
              }`}
              data-testid={`leaderboard-row-${inst.rank}`}
              onMouseEnter={() => onHover(inst)}
              onMouseLeave={() => onHover(null)}
              onClick={() => onSelect(inst)}
            >
              <span className="lb-rank">{String(inst.rank).padStart(2, "0")}</span>
              <span
                className="lb-chip"
                style={{ background: inst.color || "#B8912F" }}
                data-testid={`leaderboard-chip-${inst.rank}`}
              />
              <span className="lb-name">{inst.short}</span>
              {compareMode && cmpIndex >= 0 ? (
                <span className={`lb-cmp-badge lb-cmp-${cmpIndex}`}>
                  {cmpIndex === 0 ? "A" : "B"}
                </span>
              ) : showPillar ? (
                <span className="lb-score" data-testid={`lb-pillar-${inst.rank}`}>
                  {pillarVal == null ? "N/A" : pillarVal.toFixed(1)}
                </span>
              ) : (
                <span className="lb-score">{inst.resiliency_rating.toFixed(2)}</span>
              )}
            </button>
          );
        })}
      </div>
    </aside>
  );
}
